import {
  ChangeDetectionStrategy,
  Component,
  output,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { Subject, switchMap, takeUntil, take } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { IconName } from '@core/user-interface-legacy';
import {
  BoxComponent,
  DialogService,
  IconButtonComponent,
  IconComponent,
} from '@core/user-interface-legacy';

import { HttpErrorHandlerService } from '../../common';
import { FieldMappingStoreService } from '../data/src/field-mapping/field-mapping.service';
import type { FieldMapping } from '../types';
import { sourceIcon, sourceName } from '../utils';

@Component({
  selector: 'rti-field-mapping-form-field-mapping',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './field-mapping-form-field-mapping.component.html',
  styleUrls: ['./field-mapping-form-field-mapping.component.less'],
  imports: [IconButtonComponent, IconComponent, BoxComponent],
})
export class FieldMappingFormFieldMappingComponent {
  private readonly i18n = inject(I18nService);
  private readonly dialog = inject(DialogService);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly httpErrorHandlerService = inject(HttpErrorHandlerService);
  private readonly fieldMappingStore = inject(FieldMappingStoreService);

  public readonly fieldMapping = input<FieldMapping>(undefined!);
  public readonly integrationKind = input<string>(undefined!);
  public fieldMappingDeleted = output<number>();

  public icon: IconName;
  public title: string;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.icon = this.getIcon();
    this.title = this.getTitle();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public delete(): void {
    this.confirm$()
      .pipe(
        take(1),
        filterBoolean,
        switchMap(() =>
          this.fieldMappingStore.deleteFieldMapping(this.integrationKind(), this.fieldMapping().id),
        ),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe({
        next: () => {
          this.fieldMappingDeleted.emit(this.fieldMapping().id);
        },
        error: error => {
          this.httpErrorHandlerService.default(error);
        },
      });
  }

  private getTitle(): string {
    const source = {
      name: this.fieldMapping().data.name,
      type: this.fieldMapping().source,
      metadata: this.fieldMapping().data.metadata,
    };

    return `${this.i18n.translate(
      `rti.field_mapping.source.${this.fieldMapping().source}`,
    )}: ${sourceName(source, this.i18n)}`;
  }

  private confirm$(): Observable<boolean> {
    const title = this.i18n.translate('rti.field_mapping.settings.delete.dialog.title');
    const message = this.i18n.translate('rti.field_mapping.settings.delete.dialog.message');

    return this.dialog
      .error(
        {
          title,
          message,
        },
        this.vcRef,
      )
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()));
  }

  private getIcon(): IconName {
    return sourceIcon(this.fieldMapping().source);
  }
}
